// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer, { CartLineContainerProps } from "@pacific/App/Components/Cart/CartLineContainer";
import LinesHeader from "@pacific/App/Components/Cart/LinesHeader";
import cartPageStyles from "@pacific/App/SharedStyle/CartPageStyles";
import LazyImage, { LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import { constants } from "@pacific/XDL/constants";
import { color, xScreen } from "@pacific/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: InjectableCss;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: ButtonPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    productInfoVmiBinNumberGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    vmiBinNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: SmallHeadingAndTextStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
    productImage?: ProductImageStyles;
    // XNG-Changes
    addToListLink?: LinkPresentationProps;
    quantityOrdered?: InjectableCss;
    lineInnerContainer?: CartLineContainerProps;
    image?: LazyImageProps;
    quantityOrderedMobile?: InjectableCss;
}

const containerClassName = "cartline-container";

export const orderLinesListStyles: OrderLinesListStyles = {
    gridContainer: {
        css: css`
            display: block;
            width: 100%;
            &:after {
                content: "";
                clear: both;
                display: table;
            }
        `,
    },
    titleGridItem: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-bottom: 15px;
            border-bottom: 1px solid ${color("common.border")};
        `,
    },
    titleText: {
        variant: "h4",
        css: css`
            font-size: 24px;
            line-height: 26px;
            margin-top: 20px;
            margin-bottom: 0;
        `,
    },
    productInfoSectionOptionsGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 5px;
            margin-bottom: 10px;
            & .bold {
                font-weight: bold;
                margin-right: 5px;
            }
        `,
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    orderLineCardActionsWide: {
        above: "sm",
    },
    orderLineCardActionsNarrow: {
        below: "md",
    },
    productInfoTotalsGridContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
            @media print {
                flex-direction: row;
                gap: 110px;
            }
        `,
    },

    orderLineCardImage: {
        css: css`
            width: 88px;
            height: 88px;
            &&& {
                img {
                    overflow: hidden;
                    max-height: 100%;
                    height: auto;
                }
            }
        `,
    },
    quantityOrdered: {
        css: css`
            input {
                max-width: 55px;
                text-align: center;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                display: none;
            }
        `,
    },
    quantityOrderedMobile: {
        css: css`
            input {
                max-width: 55px;
                text-align: center;
            }
            ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                display: none;
            }
        `,
    },
    addToListLink: {
        ...cartPageStyles.cartLine?.addToListLink,
    },
    productDescriptionStyles: {
        ...cartPageStyles.cartLine?.productDescription,
    },
    priceHeadingAndText: {
        ...cartPageStyles.cartLine?.totalSmallHeadingAndText,
    },
    subtotalWithoutVatHeadingAndText: {
        ...cartPageStyles.cartLine?.totalSmallHeadingAndText,
    },
    productInfoSubtotalHeadingAndText: {
        ...cartPageStyles.cartLine?.totalSmallHeadingAndText,
    },
    lineInnerContainer: {
        extendedStyles: {
            container: {
                css: css`
                    @media print {
                        padding: 10px 0;
                        &.${containerClassName}-desktop {
                            display: none;
                        }
                        &.${containerClassName}-mobile {
                            display: block;
                            width: 100%;
                        }
                    }
                `,
            },
            description: {
                css: css`
                    @media print {
                        display: block;
                        width: 100%;
                    }
                `,
            },
            rightSide: {
                css: css`
                    @media print {
                        display: block;
                        width: 100%;
                        max-width: unset;
                    }
                `,
            },
            leftSideInfo: {
                css: css`
                    @media print {
                        display: block;
                    }
                `,
            },
            priceSection: {
                css: css`
                    @media print {
                        margin-bottom: 0;
                        width: calc(100% - 110px);
                        justify-self: flex-end;
                    }
                `,
            },
            price: {
                css: css`
                    @media print {
                        p[data-test-selector="heading"] {
                            margin-bottom: 0 !important;
                        }
                    }
                `,
            },
            total: {
                css: css`
                    @media print {
                        p[data-test-selector="heading"] {
                            margin-bottom: 0 !important;
                        }
                    }
                `,
            },
        },
    },
    image: {
        transformations: {
            width: 100,
        },
        css: css`
            object-fit: cover;
            width: 88px;
            height: 88px;
        `,
        urlOptions: "t_prdsm",
    } as any,
    productBrandStyles: {
        nameText: {
            css: css`
                @media print {
                    display: block;
                }
            `,
        },
    },
    productInfoQtyShippedHeadingAndText: {
        heading: {
            css: css`
                white-space: nowrap;
                @media print {
                    font-size: 12px;
                    font-weight: normal;
                }
            `,
        },
        text: {
            css: css`
                @media print {
                    font-size: 14px;
                    font-weight: bold;
                }
            `,
        },
    },
    productInfoQtyOrderedHeadingAndText: {
        heading: {
            css: css`
                white-space: nowrap;
                @media print {
                    font-size: 12px;
                    font-weight: normal;
                }
            `,
        },
        text: {
            css: css`
                @media print {
                    font-size: 14px;
                    font-weight: bold;
                }
            `,
        },
    },
    orderLineNotes: {
        text: {
            css: css`
                @media print {
                    margin-bottom: 5px;
                }
            `,
        },
    },
};

// XNG-Changes: promotion to be in Order Line Product Info component
const OrderLineProductInfo = ({
    orderLine,
    order,
    styles,
    vmiLocationId,
}: {
    orderLine: OrderLineModel;
    order: OrderModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <Typography {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography className="bold">{option.sectionName}:</Typography>
                {option.optionName}
            </Typography>
        ));
    }

    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map((promotion, index) => (
                <StyledWrapper key={promotion.id}>
                    <Typography>{`${translate("Promotion")} #${index + 1}:`}</Typography>
                    <Typography {...styles.orderLineInfoPromotionText}>{promotion.name}</Typography>
                </StyledWrapper>
            ));
    }

    return (
        <>
            {orderLine.brand && (
                <ProductBrand brand={orderLine.brand} showLogo={false} extendedStyles={styles.productBrandStyles} />
            )}
            {vmiLocationId && orderLine.isActiveVmiBin && (
                <VmiBinDetailTypeLink
                    title={orderLine.shortDescription}
                    locationId={vmiLocationId}
                    id={orderLine.vmiBinId}
                />
            )}
            {vmiLocationId && !orderLine.isActiveVmiBin && <Typography as="p">{orderLine.shortDescription}</Typography>}
            {!vmiLocationId && (
                <ProductDescription product={orderLine} extendedStyles={styles.productDescriptionStyles} />
            )}
            <ProductPartNumbers
                {...{ showERP: true }}
                productNumber={orderLine.productErpNumber}
                customerProductNumber={orderLine.customerProductNumber}
                manufacturerItem={orderLine.manufacturerItem}
            />
            {sectionOptions.length > 0 && (
                <StyledWrapper {...styles.productInfoSectionOptionsGridItem}>{sectionOptions}</StyledWrapper>
            )}
            {promotions && promotions.length > 0 && <StyledWrapper>{promotions}</StyledWrapper>}
            {orderLine.vmiBinNumber && (
                <StyledWrapper {...styles.productInfoVmiBinNumberGridItem}>
                    <SmallHeadingAndText
                        extendedStyles={styles.vmiBinNumberHeadingAndText}
                        heading={translate("Bin #")}
                        text={orderLine.vmiBinNumber}
                    />
                </StyledWrapper>
            )}
        </>
    );
};

const mapStateToOrderLineInfoProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

// XNG-Change: splitting qty, price and total to follow the new structure pattern
const OrderLineInfoProto = ({
    orderLine,
    enableVat,
    vatPriceDisplay,
    styles,
    // XNG-Changes
    order,
}: {
    orderLine: OrderLineModel;
    enableVat: boolean;
    vatPriceDisplay: string;
    styles: OrderLinesListStyles;
    // XNG-Changes
    order: OrderModel;
}) => {
    // XNG-Changes: get showPricing from context state
    const showPricing = useAppSelector(state => state.context.showPricing);

    // XNG-Changes: Move promotions to OrderLineProductInfo
    // let promotions: JSX.Element[] = [];
    // if (order.orderPromotions) {
    //     promotions = order.orderPromotions
    //         .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
    //         .map(promotion => (
    //             <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
    //                 {promotion.name}
    //             </Typography>
    //         ));
    // }
    // {promotions.length > 0 && (
    //     <GridItem {...styles.orderLineInfoPromotionGridItem}>
    //         <ul {...styles.orderLineInfoPromotionList}>{promotions}</ul>
    //     </GridItem>
    // )}

    return {
        Qty: (
            <StyledWrapper {...styles.productInfoTotalsGridContainer}>
                <SmallHeadingAndText
                    heading={translate("Ordered")}
                    text={orderLine.qtyOrdered}
                    extendedStyles={styles.productInfoQtyOrderedHeadingAndText}
                />
                <SmallHeadingAndText
                    heading={translate("Shipped")}
                    text={orderLine.qtyShipped}
                    extendedStyles={styles.productInfoQtyShippedHeadingAndText}
                />
                {/* <TextField
                    value={orderLine.qtyOrdered}
                    label={translate("Ordered")}
                    disabled={true}
                />
                <TextField
                    value={orderLine.qtyShipped}
                    label={translate("Shipped")}
                    disabled={true}
                /> */}
            </StyledWrapper>
        ),
        QtyMobile: (
            <StyledWrapper {...styles.productInfoTotalsGridContainer}>
                <SmallHeadingAndText
                    heading={translate("QTY Ordered")}
                    text={orderLine.qtyOrdered}
                    extendedStyles={styles.productInfoQtyOrderedHeadingAndText}
                />
                <SmallHeadingAndText
                    heading={translate("QTY Shipped")}
                    text={orderLine.qtyShipped}
                    extendedStyles={styles.productInfoQtyShippedHeadingAndText}
                />
            </StyledWrapper>
        ),
        Price: (
            <StyledWrapper data-test-selector="productOrderPriceContainer">
                {showPricing && (
                    <>
                        <SmallHeadingAndText
                            extendedStyles={styles.priceHeadingAndText}
                            heading={translate("Price")}
                            text={
                                (enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? orderLine.unitPriceWithVatDisplay
                                    : orderLine.unitPriceDisplay) +
                                (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")
                            }
                        />
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${orderLine.taxRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.secondaryPriceText} as="p">
                                            {orderLine.unitPriceDisplay +
                                                (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")}
                                        </Typography>
                                        <Typography as="p" {...styles.secondaryVatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </StyledWrapper>
        ),
        Total: (
            <StyledWrapper>
                {showPricing && (
                    <>
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <SmallHeadingAndText
                                extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                heading={`${translate("Total")} (${translate("Ex. VAT")})`}
                                text={orderLine.extendedUnitNetPriceDisplay}
                            />
                        )}
                        <SmallHeadingAndText
                            extendedStyles={styles.productInfoSubtotalHeadingAndText}
                            heading={translate("Total")}
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? orderLine.netPriceWithVatDisplay
                                    : orderLine.extendedUnitNetPriceDisplay
                            }
                        />
                    </>
                )}
            </StyledWrapper>
        ),
    };
};

const OrderLineNotes = ({
    orderLine,
    extendedStyles,
}: {
    orderLine: OrderLineModel;
    extendedStyles?: SmallHeadingAndTextStyles;
}) => {
    return orderLine.notes ? (
        <SmallHeadingAndText extendedStyles={extendedStyles} heading={translate("Line Notes")} text={orderLine.notes} />
    ) : null;
};

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    className?: string;
}) => {
    // XNG-Changes
    const { enableVat, vatPriceDisplay } = useAppSelector(state => ({
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    }));

    const { orderLine, order, wishListSettings, vmiBinDetailsPageUrl, styles, setAddToListModalIsOpen, addToWishList } =
        props;
    const toasterContext = React.useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const orderLineInfo = OrderLineInfoProto({
        orderLine,
        order,
        styles,
        enableVat,
        vatPriceDisplay,
    });

    const isShareEntity = getIsShareEntity();

    // XNG-Changes: using cartLine container global structure
    return (
        <CartLineContainer data-test-selector={`orderLineCard${orderLine.productId}`} {...styles.lineInnerContainer}>
            <CartLineContainer.Image>
                {orderLine.productUri && orderLine.isActiveProduct && !order.vmiLocationId && (
                    <Clickable
                        {...styles.orderLineCardImage}
                        href={orderLine.productUri}
                        aria-label="share entity clickable"
                    >
                        <LazyImage
                            src={orderLine.mediumImagePath}
                            altText={orderLine.altText}
                            disableLazyLoad={true}
                            isShareEntity={isShareEntity}
                            {...styles.image}
                        />
                    </Clickable>
                )}
                {orderLine.productUri && orderLine.isActiveProduct && order.vmiLocationId && orderLine.isActiveVmiBin && (
                    <Link
                        {...styles.productImageLink}
                        href={`${vmiBinDetailsPageUrl}?locationId=${order.vmiLocationId}&id=${orderLine.vmiBinId}`}
                    >
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    </Link>
                )}
                {orderLine.productUri &&
                    orderLine.isActiveProduct &&
                    order.vmiLocationId &&
                    !orderLine.isActiveVmiBin && (
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    )}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                <OrderLineProductInfo
                    orderLine={orderLine}
                    order={order}
                    styles={styles}
                    vmiLocationId={order.vmiLocationId}
                />
                <OrderLineNotes orderLine={orderLine} extendedStyles={styles.orderLineNotes} />
            </CartLineContainer.Description>
            <CartLineContainer.Qty>
                <StyledWrapper {...styles.quantityOrdered}>{orderLineInfo.Qty}</StyledWrapper>
                <StyledWrapper {...styles.quantityOrderedMobile}>{orderLineInfo.QtyMobile}</StyledWrapper>
            </CartLineContainer.Qty>
            <CartLineContainer.Price>{orderLineInfo.Price}</CartLineContainer.Price>
            <CartLineContainer.Total>{orderLineInfo.Total}</CartLineContainer.Total>
            <CartLineContainer.AddToList>
                {orderLine.canAddToWishlist && (
                    <Link {...styles.addToListLink} onClick={addToListClickHandler} aria-label="add to list">
                        {translate("Add to List")}
                    </Link>
                )}
            </CartLineContainer.AddToList>
        </CartLineContainer>
    );
};

const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    vmiBinDetailsPageUrl,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));
    const isShareEntity = getIsShareEntity();

    if (!order || !order.orderLines) {
        return null;
    }

    // XNG-Changes: using cartLine container global structure
    return (
        <StyledWrapper {...styles.gridContainer} className={!isShareEntity ? "print-view" : ""}>
            <LinesHeader />
            {order.orderLines.map((orderLine, index) => (
                <OrderLineCard
                    orderLine={orderLine}
                    order={order}
                    wishListSettings={wishListSettings}
                    vmiBinDetailsPageUrl={vmiBinDetailsPageUrl}
                    styles={styles}
                    setAddToListModalIsOpen={setAddToListModalIsOpen}
                    addToWishList={addToWishList}
                    className={`${index === 0 ? "first-item" : ""}`}
                    key={orderLine.id}
                />
            ))}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
